import { useCallback, useEffect, useRef, useState } from 'react'
import './NavTable.css'

// A lightweight, dependency-free table with resizable columns for the nav list
// area. Columns are described declaratively; the last column flexes to fill the
// remaining width while every earlier column has a draggable right edge. Widths
// persist per `storageKey` so a page's column layout sticks between visits.
//
// columns: [{ key, header, width?, minWidth?, render?(row) }]
function loadWidths(storageKey, columns) {
  const fallback = columns.slice(0, -1).map((col) => col.width ?? 160)
  if (!storageKey) return fallback
  try {
    const saved = JSON.parse(window.localStorage.getItem(storageKey))
    if (Array.isArray(saved) && saved.length === fallback.length) return saved
  } catch {
    /* ignore malformed storage */
  }
  return fallback
}

function NavTable({
  columns,
  rows,
  getRowId,
  onRowClick,
  selectedId,
  storageKey,
  emptyLabel = 'Nothing here yet.',
  showHeader = true,
}) {
  const [widths, setWidths] = useState(() => loadWidths(storageKey, columns))
  const dragRef = useRef(null)

  useEffect(() => {
    if (!storageKey) return
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(widths))
    } catch {
      /* ignore quota / privacy-mode errors */
    }
  }, [storageKey, widths])

  const onPointerMove = useCallback(
    (event) => {
      const drag = dragRef.current
      if (!drag) return
      const min = columns[drag.index].minWidth ?? 80
      const next = Math.max(min, drag.startWidth + (event.clientX - drag.startX))
      setWidths((prev) => {
        const copy = [...prev]
        copy[drag.index] = next
        return copy
      })
    },
    [columns]
  )

  const onPointerUp = useCallback(() => {
    dragRef.current = null
    window.removeEventListener('pointermove', onPointerMove)
    window.removeEventListener('pointerup', onPointerUp)
  }, [onPointerMove])

  // Clean up listeners if the component unmounts mid-drag.
  useEffect(() => () => onPointerUp(), [onPointerUp])

  const startDrag = (index) => (event) => {
    event.preventDefault()
    event.stopPropagation()
    dragRef.current = { index, startX: event.clientX, startWidth: widths[index] }
    window.addEventListener('pointermove', onPointerMove)
    window.addEventListener('pointerup', onPointerUp)
  }

  const template = [...widths.map((w) => `${w}px`), 'minmax(80px, 1fr)'].join(' ')
  const lastIndex = columns.length - 1

  return (
    <div className="nav-table">
      {showHeader && (
        <div className="nav-table-head" style={{ gridTemplateColumns: template }}>
          {columns.map((col, i) => (
            <div className="nav-table-hcell" key={col.key}>
              <span className="nav-table-htext">{col.header}</span>
              {i < lastIndex && (
                <span
                  className="nav-table-resizer"
                  onPointerDown={startDrag(i)}
                  onClick={(event) => event.stopPropagation()}
                />
              )}
            </div>
          ))}
        </div>
      )}

      <div className="nav-table-body">
        {rows.length === 0 ? (
          <div className="nav-table-empty">{emptyLabel}</div>
        ) : (
          rows.map((row) => {
            const id = getRowId(row)
            return (
              <div
                key={id}
                className={'nav-table-row' + (id === selectedId ? ' is-selected' : '')}
                style={{ gridTemplateColumns: template }}
                onClick={() => onRowClick?.(row)}
              >
                {columns.map((col) => (
                  <div className="nav-table-cell" key={col.key}>
                    {col.render ? col.render(row) : row[col.key]}
                  </div>
                ))}
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}

export default NavTable
